import React, { useEffect, useRef, useState } from "react";

/**
 * `E.8` (Ф4, макет `Activation.dc.html`): код активации — шесть ячеек.
 *
 * Одно поле на шесть цифр читалось как «введите строку»: человек вставлял код из
 * письма и не видел, все ли цифры попали. Ячейки показывают это по одной цифре:
 *
 * - вставка `481516` заполняет все шесть ячеек (лишние цифры отбрасываются);
 * - вводятся только цифры — буква не появляется в ячейке;
 * - `Backspace` в пустой ячейке возвращает к предыдущей.
 *
 * Скрытое поле `id` остаётся: форма и её проверки работают с одним значением
 * `value`, как прежде (`#activation-code`).
 */

export type CodeCellsProps = {
  /** Значение целиком: только цифры, слева направо. */
  value: string;
  /** Сколько ячеек — столько же цифр в коде (`ACTIVATION_CODE_LENGTH`). */
  length: number;
  onChange: (value: string) => void;
  /** `id` скрытого поля — для подписи и проверок формы. */
  id?: string;
};

function splitDigits(value: string, length: number): string[] {
  const chars = value.replace(/\D/g, "").slice(0, length).split("");

  return Array.from({ length }, (_, index) => chars[index] ?? "");
}

export default function CodeCells({
  value,
  length,
  onChange,
  id = "activation-code",
}: CodeCellsProps) {
  const [cells, setCells] = useState<string[]>(() => splitDigits(value, length));
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const normalized = value.replace(/\D/g, "").slice(0, length);

  // Значение пришло снаружи (скрытое поле, автозаполнение) — ячейки догоняют.
  useEffect(() => {
    if (cells.join("") !== normalized) {
      setCells(splitDigits(normalized, length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalized, length]);

  const emit = (next: string[]) => {
    setCells(next);
    onChange(next.join(""));
  };

  /** Записать цифры начиная с ячейки `from`; хвост не трогаем. */
  const writeAt = (from: number, digits: string): string[] => {
    const next = cells.slice();

    for (let i = 0; i < digits.length && from + i < length; i += 1) {
      next[from + i] = digits[i];
    }

    return next;
  };

  const focusAt = (index: number) => {
    const target = inputs.current[Math.max(0, Math.min(length - 1, index))];

    target?.focus();
    target?.select?.();
  };

  const handleChange =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const digits = event.target.value.replace(/\D/g, "");

      // Пусто — ячейка очищена (в том числе `Backspace`).
      if (digits === "") {
        const next = cells.slice();
        next[index] = "";
        emit(next);
        return;
      }

      // Вставили или набрали несколько цифр в одну ячейку — раскладываем по
      // следующим: именно так работает вставка кода из письма.
      const next = writeAt(index, digits);
      emit(next);
      focusAt(index + digits.length);
    };

  const handleKeyDown =
    (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Backspace" && cells[index] === "") {
        event.preventDefault();
        focusAt(index - 1);
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        focusAt(index - 1);
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        focusAt(index + 1);
      }
    };

  const handlePaste =
    (index: number) => (event: React.ClipboardEvent<HTMLInputElement>) => {
      const digits = (event.clipboardData?.getData("text") ?? "").replace(/\D/g, "");

      if (!digits) return;

      event.preventDefault();

      // Вставка с любой ячейки заполняет все ячейки: код из письма приходит
      // целиком, и человек не должен попадать в первую вручную.
      emit(writeAt(0, digits.slice(0, length)));
      focusAt(digits.length);
    };

  return (
    <div className="activation-code-cells" role="group" aria-label="код из письма, шесть цифр">
      {cells.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputs.current[index] = element;
          }}
          className="activation-input activation-code-cells__cell"
          value={digit}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste(index)}
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          aria-label={`цифра ${index + 1}`}
          data-cell={index + 1}
          autoCapitalize="none"
        />
      ))}

      {/* Скрытое поле — то же значение и тот же `id`, что были у одного поля. */}
      <input
        className="activation-code-cells__value"
        id={id}
        value={value}
        onChange={(event) =>
          onChange(event.target.value.replace(/\D/g, "").slice(0, length))
        }
        inputMode="numeric"
        autoComplete="one-time-code"
        tabIndex={-1}
        aria-label="код из письма целиком"
      />
    </div>
  );
}
